import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Animates showing and hiding a panel originating from anchorEl (topbar item).
 * Variants:
 *  - "circle": clip-path circle expanding from anchor
 *  - "depth": subtle 3D lift (no clip-path)
 *  - "slide": slide and fade from anchor direction
 */
export default function TabPanelEnter({
  anchorEl,
  variant = "circle",
  durationMs = 2000,                         // enter duration
  exitMs,                                    // exit duration (fallback = durationMs)
  ease = "cubic-bezier(.25,.9,.2,1)",
  visible = true,                            // true = enter, false = exit
  initialEntered = false,                    // exit overlay starts open when true
  unclipAfterEnter = true,                   // remove clip mask after enter
  children,
  className = "",
}) {
  const wrapRef = useRef(null);
  const [entered, setEntered] = useState(initialEntered);
  const [geo, setGeo] = useState({ cx: 0, cy: 0, r: 0, dirX: 0 });
  const [clipped, setClipped] = useState(variant === "circle"); // clip only for circle

  const recalc = () => {
    const w = wrapRef.current?.getBoundingClientRect();
    const a = anchorEl?.getBoundingClientRect();
    if (!w) return;

    if (!a) {
      // Fallback: start top left with slight left direction
      const r = Math.hypot(w.width, w.height) * 1.1;
      setGeo({ cx: 24, cy: 24, r, dirX: -1 });
      return;
    }

    const cx = (a.left + a.width / 2) - w.left;
    const cy = (a.top + a.height / 2) - w.top;
    const r = Math.hypot(w.width, w.height) * 1.1;
    const dirX = cx < w.width / 2 ? 1 : -1; // direction for slide/depth
    setGeo({ cx, cy, r, dirX });
  };

  useLayoutEffect(() => { recalc(); }, [anchorEl]);
  useEffect(() => {
    const onResize = () => recalc();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Reacts to panel size changes (e.g., "Show more steps")
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(() => recalc());
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Mount: two-frame sequence (initial state then target)
  useEffect(() => {
    let id1 = 0, id2 = 0;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setEntered(visible));
    });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slightly decouple later visibility changes
  useEffect(() => {
    let id = 0;
    id = requestAnimationFrame(() => setEntered(visible));
    return () => cancelAnimationFrame(id);
  }, [visible]);

  // Keep clip active only during animation
  useEffect(() => {
    if (variant !== "circle") return;
    if (!unclipAfterEnter) { setClipped(true); return; } // always clip when desired

    if (visible) {
      // ENTER: enable clip, disable after end
      setClipped(true);
      const t = setTimeout(() => setClipped(false), (durationMs ?? 0) + 30);
      return () => clearTimeout(t);
    } else {
      // EXIT: re-enable clipping for reverse
      setClipped(true);
    }
  }, [visible, durationMs, variant, unclipAfterEnter]);

  const commonStyle = {
    transitionDuration: `${entered ? (durationMs ?? 0) : (exitMs ?? durationMs ?? 0)}ms`,
    transitionTimingFunction: ease,
  };

  const states = (() => {
    if (variant === "circle") {
      return {
        className:
          "relative will-change-[clip-path,transform,opacity] transition-[clip-path,transform,opacity]",
        style: {
          ...commonStyle,
          clipPath: clipped
            ? `circle(${entered ? `${geo.r}px` : `0px`} at ${geo.cx}px ${geo.cy}px)`
            : "none",
          transformOrigin: "top left",
          transform: entered
            ? "translateY(0) scale(1)"
            : "translateY(6px) scale(0.985)",
          opacity: entered ? 1 : 0,
        },
      };
    }
    if (variant === "depth") {
      return {
        className:
          "relative will-change-[transform,opacity,filter] transition-[transform,opacity,filter]",
        style: {
          ...commonStyle,
          transformOrigin: geo.dirX < 0 ? "right center" : "left center",
          transform: entered
            ? "translateY(0) scale(1)"
            : "translateY(10px) scale(0.985)",
          filter: entered ? "blur(0px)" : "blur(6px)",
          opacity: entered ? 1 : 0,
        },
      };
    }
    // slide
    return {
      className:
        "relative will-change-[transform,opacity] transition-[transform,opacity]",
      style: {
        ...commonStyle,
        transform: entered
          ? "translate(0, 0)"
          : `translate(${geo.dirX * 24}px, 8px)`,
        opacity: entered ? 1 : 0,
      },
    };
  })();

  return (
    <div ref={wrapRef} className={className}>
      <div className={states.className} style={states.style}>
        {children}
      </div>
    </div>
  );
}
